'use client';

/**
 * The one place that knows how THIS page gets installed (app-hosts plan E1
 * and E2, 2026-09-24), shared by the header CTA and the bridge's
 * `install.*` ops so an app and a person get the same answer.
 *
 * On a host (a named host or a customer's domain) the page IS the app and
 * installs itself, in this order of preference:
 *
 *   1. the captured `beforeinstallprompt` (Chromium's own dialog);
 *   2. `navigator.install()` with no arguments (the Web Install API, where
 *      it exists) for the current document;
 *   3. the instruction sheet, for a platform whose browser installs from a
 *      menu we cannot open (Safari on iOS and macOS, Firefox);
 *   4. nothing, on a browser that offers no install at all.
 *
 * On the apex or a canvas the page is Robutler, not the app, and
 * "install" means the app's own host: `navigator.install(url, id)` when the
 * browser has it, else the host opens in a new tab where the browser's
 * install control applies (the bridge resolves the host through
 * `/api/app-host/lookup` and calls `installRemote`).
 *
 * `installed` is answered from the top window's `display-mode` and from a
 * remembered TWA arrival; apps never read that themselves, since inside a
 * frame it answers for the host page. The instruction sheet is drawn by
 * `InstallSheetHost` (components/app-host/install-sheet.tsx), which
 * subscribes here: the controller only asks for it, so the ops module
 * stays free of React.
 */

import {
  detectPlatform,
  getPwaInstallPrompt,
  isStandaloneMode,
  promptPwaInstall,
  subscribePwaInstall,
  wasInstalledHere,
  type PwaPlatform,
} from '@/components/use-pwa-install';
import { hostInstallTarget, requestWebInstall, webInstallSupported } from '@/lib/platform/web-install';
import { isConsumptionOnlySession } from '@/lib/platform/twa-referrer';
import type { InstallOutcome, InstallSnapshot, InstallState } from '@/lib/workspaces/widget-ops/install';

/** Platforms whose browsers install from a menu the page cannot open. */
const SHEET_PLATFORMS: ReadonlySet<PwaPlatform> = new Set(['ios', 'macos', 'android', 'windows', 'linux']);

const sheetListeners = new Set<() => void>();

/** The instruction sheet host registers here; returns the unsubscribe. */
export function onInstallSheetRequest(cb: () => void): () => void {
  sheetListeners.add(cb);
  return () => {
    sheetListeners.delete(cb);
  };
}

/** Ask whoever draws the sheet to show it. True when somebody is listening. */
export function openInstallSheet(): boolean {
  if (sheetListeners.size === 0) return false;
  for (const cb of sheetListeners) {
    try {
      cb();
    } catch {
      /* one listener failing must not hide the sheet from the others */
    }
  }
  return true;
}

/** The self-install state of the page this code runs in. */
export function selfInstallState(): InstallState {
  if (typeof window === 'undefined') return 'unavailable';
  if (isStandaloneMode() || wasInstalledHere() || isConsumptionOnlySession()) return 'installed';
  if (getPwaInstallPrompt() || webInstallSupported()) return 'installable';
  if (SHEET_PLATFORMS.has(detectPlatform())) return 'instructions';
  return 'unavailable';
}

export function selfInstallSnapshot(): InstallSnapshot {
  return { state: selfInstallState(), platform: detectPlatform() };
}

/** Re-run whenever the captured prompt changes or the app gets installed. */
export function subscribeSelfInstall(cb: () => void): () => void {
  return subscribePwaInstall(cb);
}

/**
 * Install THIS page. Runs the platform's own prompt, sheet or nothing, in
 * the order the file note gives. Must run inside a user gesture for the
 * first two options to succeed.
 */
export async function installSelf(): Promise<InstallOutcome> {
  if (typeof window === 'undefined') return 'unavailable';
  if (isStandaloneMode() || wasInstalledHere()) return 'unavailable';
  if (getPwaInstallPrompt()) {
    const outcome = await promptPwaInstall();
    if (outcome !== 'unavailable') return outcome;
  }
  if (webInstallSupported()) {
    const outcome = await requestWebInstall(null);
    if (outcome !== 'unavailable') return outcome;
  }
  if (SHEET_PLATFORMS.has(detectPlatform()) && openInstallSheet()) return 'instructions';
  return 'unavailable';
}

/**
 * Install the app that lives at `hostOrigin` from a page that is not it:
 * the Web Install API when supported, else the host in a new tab. Both need
 * a user gesture (the popup blocker is the second one's gate).
 */
export async function installRemote(hostOrigin: string): Promise<InstallOutcome> {
  if (typeof window === 'undefined') return 'unavailable';
  if (webInstallSupported()) {
    const outcome = await requestWebInstall(hostInstallTarget(hostOrigin));
    if (outcome !== 'unavailable') return outcome;
  }
  // `noopener` makes `window.open` return null in every browser, opened or
  // blocked alike, so the return value says nothing (functional review,
  // 2026-09-24). The host tab is where the browser's own install control
  // applies, which is the "instructions" outcome; a blocked popup is the
  // one case this cannot tell apart, and it errs on the side of the tab
  // having opened rather than reporting the app as uninstallable.
  window.open(hostInstallTarget(hostOrigin).installUrl, '_blank', 'noopener');
  return 'instructions';
}
